<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  loading: { type: Boolean, default: false },
  responsibility: { type: Object, required: true },
});

const emit = defineEmits(['boleto', 'payment']);
const { t } = useI18n();
const paid = computed(() => props.responsibility.comfirmacao_pagamento === 1);
const hasCompleteBoleto = computed(
  () =>
    Boolean(props.responsibility.boleto?.enviado) &&
    Boolean(props.responsibility.data_emissao) &&
    Boolean(props.responsibility.data_envio) &&
    Boolean(props.responsibility.data_validade)
);
const canConfirm = computed(
  () => props.responsibility.acoes?.pode_confirmar && hasCompleteBoleto.value
);
const canOpenBoleto = computed(
  () =>
    props.responsibility.acoes?.pode_enviar_boleto ||
    props.responsibility.boleto?.pode_baixar
);
const formatDate = value => {
  if (!value) return t('REAL_ESTATE_FINANCE.DETAIL.EMPTY_VALUE');
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;

  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};
const dueOn = computed(() => formatDate(props.responsibility.data_validade));
const sentOn = computed(() =>
  props.responsibility.data_envio
    ? formatDate(props.responsibility.data_envio)
    : t('REAL_ESTATE_FINANCE.DETAIL.BOLETO_NOT_SENT')
);
const amount = computed(
  () =>
    props.responsibility.valor_boleto?.amount ||
    props.responsibility.valor_pagamento?.amount ||
    t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
);
const receiptName = computed(
  () => props.responsibility.boleto?.nome || props.responsibility.nome_boleto
);
const overdue = computed(() => {
  if (paid.value || !props.responsibility.data_validade) return false;
  const value = props.responsibility.data_validade;
  const normalized = /^\d{2}\/\d{2}\/\d{4}$/.test(value)
    ? value.split('/').reverse().join('-')
    : value;
  const dueDate = new Date(`${normalized.slice(0, 10)}T00:00:00`);
  return (
    !Number.isNaN(dueDate.valueOf()) &&
    dueDate < new Date().setHours(0, 0, 0, 0)
  );
});
</script>

<template>
  <article
    data-payment-responsibility-row
    class="grid gap-3 py-4 first:pt-0 sm:grid-cols-[minmax(10rem,2fr)_minmax(9rem,1fr)_minmax(7rem,1fr)_minmax(7rem,1fr)_minmax(8rem,1fr)_minmax(9rem,1fr)_auto] sm:items-center"
  >
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.PERSON') }}
      </p>
      <p class="mt-1 truncate font-semibold text-lais-text sm:mt-0">
        {{ responsibility.usuario_nome }}
      </p>
    </div>
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.PAYMENT_STATUS') }}
      </p>
      <div class="mt-1 flex flex-wrap gap-2 sm:mt-0">
        <span
          class="rounded-full px-2 py-1 text-xs font-bold"
          :class="
            paid
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-amber-100 text-amber-800'
          "
        >
          {{
            paid
              ? t('REAL_ESTATE_FINANCE.PAYMENT.CONFIRMED')
              : t('REAL_ESTATE_FINANCE.PAYMENT.PENDING')
          }}
        </span>
        <span
          v-if="overdue"
          class="rounded-full bg-orange-100 px-2 py-1 text-xs font-bold text-orange-800"
        >
          {{ t('REAL_ESTATE_FINANCE.DETAIL.OVERDUE') }}
        </span>
      </div>
    </div>
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.SENT_ON') }}
      </p>
      <p class="mt-1 text-sm text-lais-text sm:mt-0">{{ sentOn }}</p>
    </div>
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.DUE_ON') }}
      </p>
      <p class="mt-1 text-sm text-lais-text sm:mt-0">{{ dueOn }}</p>
    </div>
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.BOLETO_VALUE') }}
      </p>
      <p class="mt-1 text-sm text-lais-text sm:mt-0">{{ amount }}</p>
    </div>
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.FILE') }}
      </p>
      <button
        v-if="canOpenBoleto && receiptName"
        data-open-boleto
        type="button"
        class="mt-1 max-w-full truncate text-left text-sm font-semibold text-lais-primary hover:underline sm:mt-0"
        :disabled="loading"
        @click="emit('boleto', responsibility)"
      >
        {{ receiptName }}
      </button>
      <button
        v-else-if="canOpenBoleto"
        data-manage-boleto
        type="button"
        class="mt-1 text-left text-sm font-semibold text-lais-primary hover:underline sm:mt-0"
        :disabled="loading"
        @click="emit('boleto', responsibility)"
      >
        {{ t('REAL_ESTATE_FINANCE.BOLETO.TITLE') }}
      </button>
      <p v-else class="mt-1 text-sm text-lais-text-muted sm:mt-0">
        {{ receiptName || t('REAL_ESTATE_FINANCE.DETAIL.EMPTY_VALUE') }}
      </p>
    </div>
    <div>
      <p class="text-xs font-semibold text-lais-text-muted sm:hidden">
        {{ t('REAL_ESTATE_FINANCE.DETAIL.PAID') }}
      </p>
      <input
        data-payment-checkbox
        :aria-label="
          t('REAL_ESTATE_FINANCE.DETAIL.CONFIRM_PAYMENT', {
            name: responsibility.usuario_nome,
          })
        "
        :checked="paid"
        :disabled="loading || !canConfirm"
        type="checkbox"
        class="mt-1 size-5 rounded border-lais-border text-lais-primary focus:ring-lais-primary sm:mt-0"
        @change="emit('payment', responsibility)"
      />
    </div>
  </article>
</template>
